import { StateSetter } from 'react';
import { useLang } from 'hooks/lang';

import { Column, Row, Button } from 'components/Blocks';
import { PlusIcon } from 'components/Icons';

import SingleCalculation from './SingleCalculation';

import { getID } from 'utils/id';
import { createExpression } from '../calculations';

import { FormTeamService } from './types';

interface CostFormulaProps {
  children?: never;
  service: FormTeamService;
  setService: StateSetter<FormTeamService | null>;
}

function CostFormula({ service, setService }: CostFormulaProps): JSX.Element {
  const { t } = useLang();

  const addCalculation = (): void => {
    setService((old) => ({
      ...(old as FormTeamService),

      costCalculation: [
        ...(old as FormTeamService).costCalculation,
        { base: { ref: '', type: 'VARIABLE' }, id: getID(), operation: 'AS_TOTAL' },
      ],
    }));
  };

  const updateCalculation = (updated: FormTeamService['costCalculation'][number]): void => {
    setService((old) => ({
      ...(old as FormTeamService),

      costCalculation: (old as FormTeamService).costCalculation.map((calc) =>
        calc.id === updated.id ? updated : calc,
      ),
    }));
  };

  const removeCalculation = (removeIndex: number): void => {
    setService((old) => {
      const toUpdate = old as FormTeamService;

      const calcToRemove = toUpdate.costCalculation[removeIndex];

      return {
        ...toUpdate,

        costCalculation: toUpdate.costCalculation.filter(
          ({ id, base, value }) =>
            ![base.ref, value?.ref, id].some((removeId) => removeId === calcToRemove.id),
        ),
      };
    });
  };

  const shouldDisable = [
    !service.costVariables.find(({ type }) => type === 'CURRENCY'),
    !!service.costCalculation.find(({ operation }) => operation === 'AS_TOTAL'),
  ].some(Boolean);

  const expression = createExpression(service);

  return (
    <Column>
      <Row>
        <p>{t('cost-calculation')}</p>

        <Button format="square" round onClick={addCalculation} disabled={shouldDisable}>
          <PlusIcon size={16} />
        </Button>
      </Row>

      <p className="text-sm">{t('cost-calculation-description')}</p>

      {service.costCalculation.map((calculation, index) => (
        <SingleCalculation
          key={calculation.id}
          index={index}
          service={service}
          update={updateCalculation}
          remove={() => removeCalculation(index)}
        />
      ))}

      <p>{`${t('total-cost')} = ${expression}`}</p>
    </Column>
  );
}

export default CostFormula;
